import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {gsap, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {Mental} from "../Mental";
import {MentalNormalSymbol} from "../reel/symbols/MentalNormalSymbol";
import {ReelEnhancerData} from "../server/ReelEnhancerData";
import {IMentalGameData} from "../server/MentalData";
import {ReelModelSymbolData} from "../../framework/reel/ReelModel";
import {MentalReel} from "../reel/MentalReel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MentalGameMode} from "../server/MentalGameMode";
import {SoundManager} from "../sound/SoundManager";
import {MentalSoundConfig} from "../MentalSoundConfig";
import {NoWinPresentation} from "../noWin/NoWinPresentation";
import {MentalSymbolStates} from "../reel/MentalSymbol";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";

export class ReelStopPresentation implements FlowStatePresentation {
    private _tl: TimelineMax;
    private _gameData: IMentalGameData;
    private _processedReels: string[][] = [];
    private static _isQuickStop: boolean = false;
    private _randomFeatureBuy: boolean = false;

    constructor() {
        Mental.apiPlugin.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {
            this._randomFeatureBuy = (betData.featureName === "FREESPIN_LEVEL_THREE");
        });
    }

    public static get isQuickStop(): boolean {
        return this._isQuickStop;
    }

    public static set isQuickStop(flag: boolean) {
        this._isQuickStop = flag;
    }

    /**
     * function to check if special reel of particular reel id will open or not
     * @param reelId
     * @private
     */
    private static isSpecialSymbolReelOpen(reelId: number): boolean {
        return Mental.model.gameData.specialReelsOpen[1 % reelId];
    }

    /**
     * added wild symbols in processed reels to show sticky wild in spin out of base game after fs
     * @param reel
     * @param stickyWildPos
     */
    private static tamperProcessReel(reel: string[], stickyWildPos: number): string[] {
        if (stickyWildPos < 0) {
            return reel;
        }
        reel[stickyWildPos + 1] = "S2";
        return reel;
    }

    /**
     * function to go back and turn all dead symbol in gold
     * @param reelId
     * @private
     */
    private static tryDeadOnReelStop(reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(Mental.deadFeaturePresentation.tryDeadToGoldOnNormalReels(reelId, false, true));
        return tl;
    }

    private static putStickyMultiOnSymbols(reelIds: number): void {
        if (!ReelStopPresentation.isQuickStop) {
            return;
        }
        const gameData: IMentalGameData = Mental.model.gameData;

        for (let index: number = 0; index < reelIds; index++) {
            for (let j: number = 0; j < 3; j++) {
                if (gameData.scatter2Position[index] === j) {
                    continue;
                }

                const symbol: MentalNormalSymbol = Mental.reelArea.getReel(index).model.getReelSymbolAt(j).positionable as MentalNormalSymbol;
                if (symbol._splitSymbols.length) {
                    for (let i: number = 0; i < symbol._splitSymbols.length; i++) {
                        if (gameData.deadMultiplierPlacedPositions[index][j][i]) {
                            symbol._splitSymbols[i]._symbolMultiplierValue = gameData.deadMultiplierPlacedValue[index][j][i];
                            symbol._splitSymbols[i].showSymbolMultiplier();
                        } else { // to fix #336
                            symbol._splitSymbols[i]._symbolMultiplierValue = 1;
                            symbol._splitSymbols[i]._symbolMultiplier.visible = false;
                        }
                    }
                } else {
                    if (gameData.deadMultiplierPlacedPositions[index][j][0]) {
                        symbol._symbolMultiplierValue = gameData.deadMultiplierPlacedValue[index][j][0];
                        symbol.showSymbolMultiplier();
                    } else { // to fix #336
                        symbol._symbolMultiplierValue = 1;
                        symbol._symbolMultiplier.visible = false;
                    }
                }
            }
        }
    }

    public start(flowStateController: FlowStateController): void {
        this._gameData = Mental.model.gameData;
        Mental.deadFeatureStickyMultiplier._stickySymbolCanBeUsed = [0, 0, 0, 0, 0, 0];
        Mental.deadFeaturePresentation.resetActiveIndex();
        this._processedReels = this._gameData.processedReels;

        for (let i = 0; i < 2; i++) {
            const reelId: number = i == 0 ? 1 : 3;
            Mental.reelArea.getReel(reelId).layerSet.layers[0].position.y = 99;
        }

        this._tl = new TimelineMax({
            pause: true,
            onComplete: () => {
                flowStateController.finish();
                if (this._randomFeatureBuy) {
                    Mental.randomFeatureBuy.hidePresentation();
                }
            }
        });

        flowStateController.setSkipCallback(() => this.skip());

        const reelEnhancerData: ReelEnhancerData = Mental.model.reelEnhancerData!;
        let isNearWinAnticipationPlayed: boolean = false;

        // add all the time for all FF if there is an FF on it, before the reels starts stopping.
        for (let i: number = 0; i < 5; i++) {
            // #19
            this._tl.add(Mental.fireFrame.showFireFrame(reelEnhancerData._fireFrameOnReels[i], i));
        }

        if (this._randomFeatureBuy) {
            let index: number = 0;
            switch (this._gameData.nextMode) {
                case MentalGameMode.SUPER_FREESPIN:
                    index = 2;
                    break;
                case MentalGameMode.COOL_FREESPIN:
                    index = 1;
                    break;
            }

            this._tl.add(() => {
                Mental.randomFeatureBuy.playAnimation(index);
            });
        }

        for (let i: number = 0; i < 5; i++) {
            let tl: TimelineLite = new TimelineLite();
            this.modifyProcessedReels(i);

            if (this._randomFeatureBuy) {
                tl.to({}, {duration: 0.5});
            }
            // #95, adding stop reel time to timeline for scatter anticipation
            const s2Count: number = Mental.scatterAnticipation.getS2Count();

            if (!this._gameData.wasFeatureBuy && i === 4 && Mental.scatterAnticipation.checkForNearWin()) {
                tl.add(TweenLite.to(this, {
                    duration: s2Count === 0 ? 1.5 : (s2Count === 1 ? 2 : 2.5)
                }));
            }

            tl.add(this.stopReel(i));

            tl.add(() => {
                // #95
                Mental.reelArea.getReel(4).model.stepDuration = 0.04;
                if (i === 3 && !(ReelStopPresentation._isQuickStop) && Mental.scatterAnticipation.checkForNearWin()) {
                    if (this._gameData.wasFeatureBuy || ReelStopPresentation._isQuickStop) {
                        Mental.reelArea.getReel(4).model.stepDuration = 0.04;
                    } else {
                        Mental.reelArea.getReel(4).model.stepDuration = s2Count === 2 ? 0.01 : 0.02;
                    }
                    Mental.scatterAnticipation.playScatterAnticipation();

                    // #334
                    isNearWinAnticipationPlayed = true;
                    SoundManager.playSoundEffect(MentalSoundConfig.ANTICIPATION_ELECTRIC, 0, 1);
                }
            });

            // #95
            if (i === 4 && Mental.scatterAnticipation.checkForNearWin()) {
                tl.add(() => {
                    Mental.scatterAnticipation.stopScatterAnticipation();

                    // #334
                    if (isNearWinAnticipationPlayed) {
                        SoundManager.fadeSound(MentalSoundConfig.ANTICIPATION_ELECTRIC, 1, 0, 0.3);

                        SoundManager.playSoundEffect(MentalSoundConfig.ANTICIPATION_ELECTRIC_END);
                    }
                });
            }

            // adding splitting of symbols to timeline
            if (reelEnhancerData.isFireFrame[i]) {
                tl.add(this.splitAndResizeFireFrameSymbols(i, reelEnhancerData));

                // some time offset so that above settles and it also helps in JU times.
                tl.to(this, {duration: 0.05});

                // #154
                tl.add(this.tryAddingPlusOneTextAnimation(i));

                // go back and turn all dead symbol in gold
                tl.add(ReelStopPresentation.tryDeadOnReelStop(i));

                if (this._gameData.mode === MentalGameMode.COOL_FREESPIN || this._gameData.mode === MentalGameMode.SUPER_FREESPIN) {
                    tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnimOnReelStop(this._gameData.stickyDeadSymCounter, i));
                }
            } else {
                // #154
                if (i === 4) {
                    tl.add(this.tryAddingPlusOneTextAnimation(i));
                }

                // go back and turn all dead symbol in gold
                tl.add(ReelStopPresentation.tryDeadOnReelStop(i));
                if (this._gameData.mode === MentalGameMode.COOL_FREESPIN || this._gameData.mode === MentalGameMode.SUPER_FREESPIN) {
                    tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnimOnReelStop(this._gameData.stickyDeadSymCounter, i));
                }

                tl.add(() => NoWinPresentation.checkForNoWinSyms(i));
            }

            this._tl.add(tl);
        }

        // hiding all fire frames on reel stop
        this._tl.add(() => Mental.fireFrame.hideFireFrame());

        // check for any JU padding and add that in the start.
        const padding: number = Mental.gameFlowController.calculatePaddingDuration(this._tl.totalDuration());
        if (padding > 0) {
            this._tl.shiftChildren(padding + (Mental.reelArea.getReel(0).model.stepDuration) * 3); // Adding 120ms extra because of tiny variations depending on frame-rates and i guess rounding errors. Have observed spins with roughly 10-15 ms too little. So adding a bit more just to be safe.
        }

        // handling fast spin.
        if (Mental.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            this._tl.timeScale(1.5);
        }

        this._tl.play(0);
    }

    /**
     * function to stop reel.
     * @param reelId
     */
    private stopReel(reelId: number): TimelineLite {
        const reel: MentalReel = Mental.reelArea.getReel(reelId);
        return reel.model.stop(this._processedReels[reelId]).add(() => {
            // to fix #273
            if (this._gameData.mode === MentalGameMode.SUPER_FREESPIN || this._gameData.mode === MentalGameMode.COOL_FREESPIN) {
                gsap.delayedCall(0.2, ReelStopPresentation.putStickyMultiOnSymbols, [reelId]);
            }
        });
    }

    /**
     * modify reel before reel stop
     * @param reelId
     */
    private modifyProcessedReels(reelId: number): void {
        let reel: string[] = this._gameData.processedReels[reelId];

        if (this._gameData.mode.indexOf("FREESPIN") > -1) {
            reel = ReelStopPresentation.tamperProcessReel(reel, this._gameData.stickyScatter2[reelId]);
        }

        if (reelId === 1 && this._gameData.scatter2Position[1] > -1) {
            this._gameData.firstS2SymbolName.push("S2");
        } else if (reelId === 3 && this._gameData.scatter2Position[3] > -1) {
            this._gameData.secondS2SymbolName.push("S2");
        }

        if (Mental.reelArea.getReel(reelId).isSpecialSymbolReel && ReelStopPresentation.isSpecialSymbolReelOpen(reelId)) {
            // added this check to avoid xNudge,xWays.
            if (this._gameData.specialReelsSymbols[1 % reelId].indexOf("X") === -1) {
                reel[3] = this._gameData.specialReelsSymbols[1 % reelId];
            }
        }
    }

    /**
     * function to split and resize symbols in case of fire frame
     * @param reelId
     * @param reelEnhancerData
     */
    private splitAndResizeFireFrameSymbols(reelId: number, reelEnhancerData: ReelEnhancerData): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            fireFrameOnReel: boolean[] = reelEnhancerData._fireFrameOnReels[reelId];

        // #137
        tl.add(() => {
            SoundManager.stopSoundEffect(MentalSoundConfig.REEL_FIRE[reelId]);
            SoundManager.playSoundEffect(MentalSoundConfig.REEL_SPLIT[reelId]);
        }, 0);

        // Split all symbols
        for (let i: number = 0; i < fireFrameOnReel.length; i++) {
            const value: boolean = fireFrameOnReel[i];
            if (!value) {
                continue;
            }
            tl.add(() => {
                const symbolSplitFire: TimelineSprite = new TimelineSprite(Mental.assetLoader.getTextures("symbolSplitFire"), 45);
                NolimitApplication.getLayerByName(Mental.LAYERS.X_NUDGE)?.addChild(symbolSplitFire);
                symbolSplitFire.anchor.set(0.5);
                symbolSplitFire.position.x = symbolSplitFire.width * 0.5 + 3;
                symbolSplitFire.blendMode = PIXI.BLEND_MODES.ADD;
                symbolSplitFire.scale.set(2);

                const offsetValue: number = Mental.reelArea.getReel(reelId).isSpecialSymbolReel ? 1.5 : 1;
                symbolSplitFire.position.y = (199 * (i + offsetValue));
                symbolSplitFire.position.x = (144 * reelId) + symbolSplitFire.width / 4;
                symbolSplitFire.getAnimationAutoShowHide(true, true, void (0), void (0), () => {
                    NolimitApplication.getLayerByName(Mental.LAYERS.X_NUDGE)?.removeChild(symbolSplitFire);
                });
            }, 0);
            tl.add([() => {
                const symbolData: ReelModelSymbolData = Mental.reelArea.getReel(reelId).model.getStopSymbolAt(i);
                const symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable;
                const symName: string = symbol.symbolName;
                const size: number = 2;
                if (symName === "S2") { // handling symbolName for double features
                    if (reelId === 1) {
                        this._gameData.firstS2SymbolName = [];
                    } else {
                        this._gameData.secondS2SymbolName = [];
                    }

                    for (let j: number = 0; j < size; j++) {
                        if (reelId === 1) {
                            this._gameData.firstS2SymbolName.push("S2");
                        } else {
                            this._gameData.secondS2SymbolName.push("S2");
                        }
                    }
                }

                symbol.split(size, symName);
                symbol.allowedToResizeSegments = true;
                symbol.setStateAsPerParts(size);

                const currSymState: MentalSymbolStates = symbol.getState();

                if (symbol.isAnticipatingState(currSymState)) {
                    Mental.scatterAnticipation.dimSplitSyms(symbol, currSymState);
                }

            }], 0.15);
        }

        // #281, and it is safe to check in the evaluatedArea for this game.
        const wildIndex: number = this._gameData.evaluatedArea[reelId].indexOf("W");
        if (fireFrameOnReel[wildIndex]) {
            tl.add(() => NoWinPresentation.checkForNoWinSyms(reelId), 0.15 + 0.3); // 0.3 is for split wild landing animation.
        } else {
            tl.add(() => NoWinPresentation.checkForNoWinSyms(reelId), 0.15);
        }
        return tl;
    }

    // #154
    private tryAddingPlusOneTextAnimation(reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            symId: number = (reelId % 2 === 0 ? this._gameData.scatterPositions[reelId] : this._gameData.scatter2Position[reelId]);

        // if feature buy or Free Spin upgrade, do this on that particular reel stop.
        if ((this._gameData.nextMode !== MentalGameMode.NORMAL) && (this._gameData.wasFeatureBuy || this._gameData.mode.indexOf("FREESPIN") > -1)
            && (this._gameData.mode !== this._gameData.nextMode)
            && (symId > -1 && this._gameData.reels[reelId][symId + 1].indexOf("S") > -1) // to ensure, it's a newly landed S/S2, especially helpful in FS with sticky S2.
            && (this._gameData.stickyFires[reelId][symId] || this._gameData.fireFrames[reelId][symId])) {
            // #334
            tl.add(() => {
                SoundManager.playSoundEffect(MentalSoundConfig.PLUS_ONE);
            }, 0);

            tl.add(Mental.plusOneText.playPlusOneTextAnimation(reelId, symId));
        }
        // normal mode to any FS in non feature buy.
        else if (!this._gameData.wasFeatureBuy && reelId === 4 && (this._gameData.mode === MentalGameMode.NORMAL && this._gameData.nextMode.indexOf("FREESPIN") > -1)) {
            // do for all 5 reels
            let isPlusOneSoundPlayed: boolean = false;
            for (let i: number = 0; i <= reelId; i++) {
                const symId1: number = (i % 2 === 0 ? this._gameData.scatterPositions[i] : this._gameData.scatter2Position[i]);
                if (symId1 > -1 && this._gameData.fireFrames[i][symId1]) {
                    // #334
                    if (!isPlusOneSoundPlayed) {
                        tl.add(() => {
                            SoundManager.playSoundEffect(MentalSoundConfig.PLUS_ONE);
                        }, 0);
                        isPlusOneSoundPlayed = true;
                    }

                    tl.add(Mental.plusOneText.playPlusOneTextAnimation(i, symId1), 0); // play all simultaneously i.e time is added several times but reflected once, since time is same.
                }
            }
        }

        return tl;
    }

    /**
     * function  whenever someone quick stops during spin
     * @private
     */
    private skip(): void {
        // added some offset time to settle reels on quick stop.
        // to fix #192
        ReelStopPresentation.isQuickStop = true;
        this._tl.to({}, {duration: 0.5});

        /**
         * to fix #273 for whole reel on Abort
         */
        this._tl.add(() => {
            ReelStopPresentation.putStickyMultiOnSymbols(5);
        });

        if (this._randomFeatureBuy) { // hide random buyFeature intro
            Mental.randomFeatureBuy.hidePresentation();
        }
        this._tl.timeScale(4);
    }
}
